import { useQuery } from "@tanstack/react-query";
import { GuestCapacityCard } from "@wedding/guest-capacity";
import { useTranslation } from "react-i18next";
import { useAppNavigate } from "../lib/route-primitives";
import { useState, useEffect, useRef } from "react";
import {
  Heart, MessageCircle, Upload, Play, AlertTriangle, ChevronRight,
  UserPlus, KeyRound, ImageIcon, MonitorPlay, Clock, CheckCircle2, XCircle,
  Link2, Copy, Check,
} from "lucide-react";
import QRCode from "qrcode";
import { cn } from "../lib/utils";
import { paths } from "../lib/paths";
import { useAdminApi } from "../hooks/use-admin-api";
import { useWedding } from "../contexts/wedding-context";
import { formatWeddingDate, buildScanUrl } from "@wedding/shared";
import { useActiveEvent, useEventName } from "@wedding/ui";
import type { ActivityItem } from "../lib/api";

// ─── Interfaces ──────────────────────────────────────────────
interface Stats {
  guests: number;
  guestLimit: { current: number; max: number };
  rsvp: { yes: number; no: number; maybe: number; totalHeadcount: number };
  media: { pending: number; approved: number };
}

// ─── Helpers ─────────────────────────────────────────────────
const rtfCache = new Map<string, Intl.RelativeTimeFormat>();

function formatRelativeTime(isoString: string, lang: string): string {
  const diff = Date.now() - new Date(isoString).getTime();
  try {
    let rtf = rtfCache.get(lang);
    if (!rtf) { rtf = new Intl.RelativeTimeFormat(lang, { numeric: "auto" }); rtfCache.set(lang, rtf); }
    const secs = Math.floor(diff / 1000);
    if (secs < 60) return rtf.format(-secs, "second");
    const mins = Math.floor(secs / 60);
    if (mins < 60) return rtf.format(-mins, "minute");
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return rtf.format(-hrs, "hour");
    return rtf.format(-Math.floor(hrs / 24), "day");
  } catch { return `${Math.floor(diff / 60000)}m`; }
}

function ActivityTypeIcon({ type }: { type: ActivityItem["type"] }) {
  if (type === "like") return (
    <div className="h-8 w-8 shrink-0 rounded-full bg-rose-100 dark:bg-rose-500/20 flex items-center justify-center">
      <Heart className="h-3.5 w-3.5 text-rose-500" />
    </div>
  );
  if (type === "comment") return (
    <div className="h-8 w-8 shrink-0 rounded-full bg-sky-100 dark:bg-sky-500/20 flex items-center justify-center">
      <MessageCircle className="h-3.5 w-3.5 text-sky-500" />
    </div>
  );
  return (
    <div className="h-8 w-8 shrink-0 rounded-full bg-emerald-100 dark:bg-emerald-500/20 flex items-center justify-center">
      <Upload className="h-3.5 w-3.5 text-emerald-500" />
    </div>
  );
}

// ─── RSVP Segmented Bar ───────────────────────────────────────
function RsvpBar({ yes, no, maybe }: { yes: number; no: number; maybe: number }) {
  const total = yes + no + maybe;
  if (total === 0) return <div className="h-1.5 rounded-full bg-muted/50" />;
  const yPct = (yes / total) * 100;
  const mPct = (maybe / total) * 100;
  const nPct = (no / total) * 100;
  return (
    <div className="flex h-1.5 w-full overflow-hidden rounded-full gap-px">
      {yes > 0   && <div className="bg-emerald-500 transition-all duration-700" style={{ width: `${yPct}%` }} />}
      {maybe > 0 && <div className="bg-amber-400 transition-all duration-700"   style={{ width: `${mPct}%` }} />}
      {no > 0    && <div className="bg-rose-400 transition-all duration-700"    style={{ width: `${nPct}%` }} />}
    </div>
  );
}

// ─── Guest Arc ────────────────────────────────────────────────
function GuestArc({ current, max }: { current: number; max: number }) {
  const pct = max === 0 ? 0 : Math.min(current / max, 1);
  return (
    <svg viewBox="0 0 80 46" className="w-full" aria-hidden="true">
      <path d="M 8,42 A 32,32 0 0 1 72,42"
        fill="none" stroke="currentColor" strokeWidth="5" strokeLinecap="round"
        className="text-muted/30" pathLength="100"
      />
      <path d="M 8,42 A 32,32 0 0 1 72,42"
        fill="none" stroke="currentColor" strokeWidth="5" strokeLinecap="round"
        className="text-primary" pathLength="100"
        strokeDasharray={`${pct * 100} 100`}
        style={{ transition: "stroke-dasharray 0.8s ease" }}
      />
    </svg>
  );
}

// ─── Card shell ───────────────────────────────────────────────
const card = "bg-card dark:bg-[rgba(27,24,23,0.75)] dark:backdrop-blur-xl border border-border dark:border-primary/20 shadow-sm rounded-2xl overflow-hidden";

// ─── Invite Link Card ─────────────────────────────────────────
function InviteLinkCard({ url, onSettingsClick, label, hint, copiedLabel }: {
  // `url` = DİNAMİK scan-redirect (`/j/:slug`, buildScanUrl). Hem QR görüntüsü hem
  // kopyalanan link bunu kullanır. Worker taramayı loglayıp join sayfasına 302 yapar;
  // QR/link asla final hedefi (`/:slug/join`) gömmez → sonradan sunucudan re-point edilebilir.
  url: string;
  onSettingsClick: () => void;
  label: string;
  hint: string;
  copiedLabel: string;
}) {
  const [copied, setCopied] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (!canvasRef.current) return;
    QRCode.toCanvas(canvasRef.current, url, {
      width: 160,
      margin: 1,
      color: { dark: "#8d6b5e", light: "#ffffff" },
    }).catch(() => {});
  }, [url]);

  function handleCopy() {
    void navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  return (
    <div className={card}>
      <button
        onClick={onSettingsClick}
        className="w-full flex items-center justify-between px-5 py-4 border-b border-border/60 hover:bg-primary/5 transition-colors cursor-pointer"
      >
        <div className="flex items-center gap-2">
          <Link2 className="w-4 h-4 text-primary" />
          <h2 className="text-sm font-semibold">{label}</h2>
        </div>
        <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
      </button>
      <div className="p-4 space-y-3">
        <p className="text-xs text-muted-foreground">{hint}</p>
        <div className="flex items-center gap-2 rounded-xl bg-muted/50 border border-border px-3 py-2.5">
          <span className="text-xs text-muted-foreground truncate flex-1 font-mono">{url}</span>
          <button
            onClick={handleCopy}
            className="shrink-0 p-1 rounded-md hover:bg-primary/10 transition-colors cursor-pointer"
            aria-label={copiedLabel}
          >
            {copied
              ? <Check className="w-3.5 h-3.5 text-emerald-500" />
              : <Copy className="w-3.5 h-3.5 text-muted-foreground" />
            }
          </button>
        </div>
        <div className="flex justify-center pt-1">
          <canvas ref={canvasRef} />
        </div>
      </div>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────
export function DashboardPage() {
  const { t, i18n } = useTranslation();
  const api = useAdminApi();
  const { wedding, slug } = useWedding();
  const navigate = useAppNavigate();

  const { data: statsData, isLoading: statsLoading } = useQuery({
    queryKey: ["admin", slug, "stats"],
    queryFn: () => api.get<Stats>("/stats"),
  });

  const { data: activityData, isLoading: activityLoading } = useQuery({
    queryKey: ["admin", slug, "activity"],
    queryFn: () => api.getActivity(8),
  });

  const stats = statsData?.data;
  const activities: ActivityItem[] = activityData?.data ?? [];

  const diffMs = wedding?.date_time
    ? new Date(wedding.date_time).getTime() - Date.now()
    : null;
  const daysLeft = diffMs !== null ? Math.ceil(diffMs / 86_400_000) : null;
  const isToday  = daysLeft !== null && daysLeft === 0;
  const isPast   = daysLeft !== null && daysLeft < 0;
  const isFuture = daysLeft !== null && daysLeft > 0;

  return (
    <div className="space-y-6">

      {/* ── Hero banner ── */}
      {!wedding ? (
        <div className="h-44 rounded-2xl bg-muted animate-pulse" />
      ) : (
        <div className={cn(
          "relative overflow-hidden rounded-2xl isolate",
          wedding.bg_countdown ? "h-52" : "h-44"
        )}>
          {wedding.bg_countdown ? (
            <img src={wedding.bg_countdown} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover" />
          ) : (
            <div className="absolute inset-0 bg-gradient-to-br from-[#2e1e19] via-[#3d2b25] to-[#1b1817]" />
          )}
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/40 to-black/10" />
          <div className="absolute inset-0 opacity-[0.03] bg-[url('data:image/svg+xml,%3Csvg viewBox=%220 0 200 200%22 xmlns=%22http://www.w3.org/2000/svg%22%3E%3Cfilter id=%22noise%22%3E%3CfeTurbulence type=%22fractalNoise%22 baseFrequency=%220.9%22 numOctaves=%224%22 stitchTiles=%22stitch%22/%3E%3C/filter%3E%3Crect width=%22100%25%22 height=%22100%25%22 filter=%22url(%23noise)%22/%3E%3C/svg%3E')]" />
          <div className="relative h-full flex items-center justify-between px-6 py-6 gap-4">
            <div className="min-w-0 flex-1 space-y-2">
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/40">
                {t("dashboard.welcomeBack")}
              </p>
              <h1 className="font-serif text-2xl md:text-3xl font-bold text-white leading-tight tracking-tight truncate">
                {wedding?.title ?? slug}
              </h1>
              {wedding?.date_time && (
                <p className="text-xs text-white/50 font-medium">
                  {new Date(wedding.date_time).toLocaleDateString(i18n.language, {
                    day: "numeric", month: "long", year: "numeric",
                  })}
                </p>
              )}
            </div>
            {isFuture && (
              <div className="shrink-0 text-right">
                <p className="text-5xl md:text-6xl font-bold tabular-nums text-white leading-none">{daysLeft}</p>
                <p className="text-xs font-semibold text-white/60 mt-1.5">{t("dashboard.daysLeft")}</p>
              </div>
            )}
            {isToday && (
              <div className="shrink-0 text-right">
                <p className="font-serif text-2xl font-bold text-white">🎉</p>
                <p className="text-xs font-bold text-white/60 mt-1">{t("home.countdown.today")}</p>
              </div>
            )}
            {isPast && (
              <div className="shrink-0 text-right space-y-1">
                <p className="text-2xl">🎊</p>
                <p className="text-xs font-semibold text-white/60">{t("dashboard.weddingPast")}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Misafir kotası + QR ekstra kayıt (organizatörle aynı kart) ── */}
      <GuestCapacityCard apiBase={`/api/admin/w/${slug}`} role="admin" />

      {/* ── Stats: 2-col ── */}
      {statsLoading ? (
        <div className="grid grid-cols-2 gap-4">
          {[0, 1].map(i => <div key={i} className={cn(card, "h-48 animate-pulse bg-muted")} />)}
        </div>
      ) : stats && (
        <div className="grid grid-cols-2 gap-4">

          {/* RSVP card */}
          <div className={card}>
            <div className="p-5 flex flex-col gap-4 h-full">
              <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground">RSVP</p>
              <div>
                <p className="text-4xl font-bold tabular-nums leading-none">{stats.rsvp.yes}</p>
                <p className="text-xs text-muted-foreground mt-1.5">{t("dashboard.attending")}</p>
              </div>
              <RsvpBar yes={stats.rsvp.yes} no={stats.rsvp.no} maybe={stats.rsvp.maybe} />
              <div className="space-y-1.5 mt-auto">
                {[
                  { color: "bg-emerald-500", label: t("dashboard.attending"),    val: stats.rsvp.yes   },
                  { color: "bg-amber-400",   label: t("dashboard.maybe"),        val: stats.rsvp.maybe },
                  { color: "bg-rose-400",    label: t("dashboard.notAttending"), val: stats.rsvp.no    },
                ].map(({ color, label, val }) => (
                  <div key={label} className="flex items-center justify-between text-xs">
                    <span className="flex items-center gap-1.5 text-muted-foreground min-w-0">
                      <span className={cn("w-1.5 h-1.5 rounded-full shrink-0", color)} />
                      <span className="truncate">{label}</span>
                    </span>
                    <span className="font-semibold tabular-nums ml-2">{val}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Guests card */}
          <div className={card}>
            <div className="p-5 flex flex-col gap-3 h-full">
              <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground">
                {t("dashboard.totalGuests")}
              </p>
              {stats.guestLimit?.max > 0 ? (
                <>
                  <div className="relative flex-1 flex flex-col justify-center">
                    <div className="relative w-full">
                      <GuestArc current={stats.guestLimit.current} max={stats.guestLimit.max} />
                      <div className="absolute bottom-0 inset-x-0 flex flex-col items-center pb-1">
                        <p className="text-3xl font-bold tabular-nums leading-none">{stats.guests}</p>
                        <p className="text-[10px] text-muted-foreground mt-1">{t("dashboard.totalGuests")}</p>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-1 mt-auto">
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-muted-foreground">{t("dashboard.capacityUsed")}</span>
                      <span className="font-semibold tabular-nums">
                        {Math.round((stats.guestLimit.current / stats.guestLimit.max) * 100)}%
                      </span>
                    </div>
                    <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
                      <div
                        className="h-full rounded-full bg-primary transition-all duration-700"
                        style={{ width: `${Math.min((stats.guestLimit.current / stats.guestLimit.max) * 100, 100)}%` }}
                      />
                    </div>
                    <p className="text-[10px] text-muted-foreground/60 text-right tabular-nums">
                      {stats.guestLimit.current} / {stats.guestLimit.max}
                    </p>
                  </div>
                </>
              ) : (
                <div className="flex-1 flex flex-col items-center justify-center gap-1">
                  <p className="text-5xl font-bold tabular-nums">{stats.guests}</p>
                  <p className="text-xs text-muted-foreground">{t("dashboard.totalGuests")}</p>
                </div>
              )}
            </div>
          </div>

        </div>
      )}

      {/* ── Pending alert ── */}
      {stats && stats.media.pending > 0 && (
        <button
          onClick={() => navigate(paths.wedding.moderation(slug))}
          className="w-full flex items-center gap-3 px-4 py-3.5 rounded-2xl bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/20 hover:bg-amber-100 dark:hover:bg-amber-500/15 transition-colors cursor-pointer text-left"
        >
          <div className="w-8 h-8 rounded-xl bg-amber-100 dark:bg-amber-500/20 flex items-center justify-center shrink-0">
            <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-amber-800 dark:text-amber-300">
              {stats.media.pending} {t("dashboard.pendingAlert")}
            </p>
            <p className="text-xs text-amber-600/70 dark:text-amber-400/60 mt-0.5">
              {t("dashboard.pendingAlertSub")}
            </p>
          </div>
          <ChevronRight className="w-4 h-4 text-amber-500 shrink-0" />
        </button>
      )}

      {/* ── Bottom: activity + quick actions ── */}
      <div className="lg:grid lg:grid-cols-[1fr_260px] lg:gap-4 space-y-4 lg:space-y-0">

        {/* Recent Activity */}
        <div className={card}>
          <div className="px-5 py-4 border-b border-border/60">
            <h2 className="text-sm font-semibold">{t("dashboard.activityFeed")}</h2>
          </div>
          <div className="p-3">
            {activityLoading ? (
              <div className="space-y-1">
                {[0, 1, 2, 3].map(i => (
                  <div key={i} className="flex items-center gap-3 px-3 py-3 rounded-xl">
                    <div className="h-8 w-8 rounded-full bg-muted animate-pulse shrink-0" />
                    <div className="flex-1 space-y-2">
                      <div className="h-3 rounded-md bg-muted animate-pulse" />
                      <div className="h-2.5 rounded-md bg-muted animate-pulse w-2/3" />
                    </div>
                  </div>
                ))}
              </div>
            ) : activities.length === 0 ? (
              <div className="py-10 text-center">
                <p className="text-sm text-muted-foreground">{t("dashboard.noActivity")}</p>
              </div>
            ) : (
              <div className="space-y-0.5">
                {activities.map((item) => {
                  const ctx = item.media_kind === "video" ? "video" : undefined;
                  return (
                    <button
                      key={item.id}
                      onClick={() => navigate(paths.wedding.gallery(slug))}
                      className="flex w-full items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-primary/5 transition-colors text-left cursor-pointer"
                    >
                      <ActivityTypeIcon type={item.type} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm leading-snug">
                          <span className="font-semibold">{item.guest_name ?? t("activity.unknownGuest")} </span>
                          {/* Ternary BİLEREK t()'nin İÇİNDE: ara değişkene alınırsa
                              `pnpm check:i18n-keys` argümanı Identifier görüp atlar.
                              Burada her dal literal olduğu için üçü de doğrulanır. */}
                          <span className="text-muted-foreground">
                            {t(
                              item.type === "upload" || item.type === "upload_pending"
                                ? "activity.uploaded"
                                : item.type === "like"
                                  ? "activity.liked"
                                  : "activity.commented",
                              { context: ctx }
                            )}
                          </span>
                        </p>
                        {item.content && (
                          <p className="truncate text-xs text-muted-foreground mt-0.5">"{item.content}"</p>
                        )}
                        <p className="text-[10px] text-muted-foreground/60 uppercase tracking-wide font-medium mt-0.5">
                          {formatRelativeTime(item.created_at, i18n.language)}
                        </p>
                      </div>
                      {item.media_r2_key && (
                        <div className="h-10 w-10 shrink-0 overflow-hidden rounded-full bg-muted">
                          {item.media_kind === "image" ? (
                            <img
                              src={`/api/admin/w/${slug}/media/${item.media_id}/raw`}
                              alt=""
                              width={40}
                              height={40}
                              className="h-full w-full object-cover"
                              loading="lazy"
                            />
                          ) : (
                            <div className="relative h-full w-full bg-zinc-900">
                              <video
                                src={`/api/admin/w/${slug}/media/${item.media_id}/raw`}
                                className="h-full w-full object-cover"
                                preload="metadata"
                                muted
                                playsInline
                              />
                              <div className="absolute inset-0 flex items-center justify-center">
                                <Play className="h-3 w-3 text-white/80" />
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Right column */}
        <div className="space-y-4">

          {!wedding && (
            <>
              <div className={cn(card, "h-52 animate-pulse bg-muted")} />
              <div className={cn(card, "h-48 animate-pulse bg-muted")} />
            </>
          )}

          {/* Event Status */}
          {wedding && (() => {
            const reg = wedding?.config?.registration ?? {};
            const features: { gallery?: boolean; upload?: boolean; slideshow?: boolean } = wedding?.config?.features ?? {};

            const registrationOpensAt = reg.registrationOpensAt
              ? new Date(reg.registrationOpensAt)
              : null;
            const registrationScheduled =
              !reg.allowSelfRegister && registrationOpensAt && registrationOpensAt > new Date();

            type StatusRow = {
              icon: React.ReactNode;
              label: string;
              status: "on" | "off" | "scheduled";
              detail?: string;
            };

            const rows: StatusRow[] = [
              {
                icon: <UserPlus className="w-3.5 h-3.5" />,
                label: t("registration.allowSelfRegister"),
                status: reg.allowSelfRegister ? "on" : registrationScheduled ? "scheduled" : "off",
                detail: registrationScheduled
                  ? registrationOpensAt!.toLocaleDateString(i18n.language, { day: "numeric", month: "short" })
                  : undefined,
              },
              {
                icon: <KeyRound className="w-3.5 h-3.5" />,
                label: t("registration.requireOtp"),
                status: reg.requireOtp ? "on" : "off",
              },
              {
                icon: <ImageIcon className="w-3.5 h-3.5" />,
                label: t("nav.gallery"),
                status: features.gallery !== false ? "on" : "off",
              },
              {
                icon: <Upload className="w-3.5 h-3.5" />,
                label: t("nav.upload"),
                status: features.upload !== false ? "on" : "off",
              },
              {
                icon: <MonitorPlay className="w-3.5 h-3.5" />,
                label: t("nav.slideshow"),
                status: features.slideshow !== false ? "on" : "off",
              },
            ];

            const badgeClass: Record<StatusRow["status"], string> = {
              on:        "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
              off:       "bg-rose-500/10 text-rose-500 dark:text-rose-400",
              scheduled: "bg-amber-500/10 text-amber-600 dark:text-amber-400",
            };
            const badgeLabel: Record<StatusRow["status"], string> = {
              on:        t("dashboard.statusOn"),
              off:       t("dashboard.statusOff"),
              scheduled: t("dashboard.statusScheduled"),
            };
            const BadgeIcon = ({ status }: { status: StatusRow["status"] }) =>
              status === "on" ? <CheckCircle2 className="w-3 h-3" />
                : status === "scheduled" ? <Clock className="w-3 h-3" />
                : <XCircle className="w-3 h-3" />;

            return (
              <div className={card}>
                <button
                  onClick={() => navigate(paths.wedding.settings(slug))}
                  className="w-full flex items-center justify-between px-5 py-4 border-b border-border/60 hover:bg-primary/5 transition-colors cursor-pointer"
                >
                  <h2 className="text-sm font-semibold">{t("dashboard.eventStatus")}</h2>
                  <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
                </button>
                <div className="divide-y divide-border/40">
                  {rows.map((row) => (
                    <div key={row.label} className="flex items-center gap-3 px-5 py-3">
                      <span className="text-muted-foreground shrink-0">{row.icon}</span>
                      <span className="text-xs text-foreground flex-1 min-w-0 truncate">{row.label}</span>
                      <span className={cn(
                        "flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full shrink-0",
                        badgeClass[row.status]
                      )}>
                        <BadgeIcon status={row.status} />
                        {row.detail ?? badgeLabel[row.status]}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}

          {/* Invite Link */}
          {wedding && (
            <InviteLinkCard
              url={buildScanUrl(
                (import.meta.env.VITE_WEB_BASE_URL as string | undefined)?.replace(/\/$/, "") ?? "http://localhost:5173",
                slug,
              )}
              onSettingsClick={() => navigate(paths.wedding.settings(slug))}
              label={t("dashboard.inviteLink")}
              hint={t("dashboard.inviteLinkHint")}
              copiedLabel={t("dashboard.copied")}
            />
          )}

        </div>
      </div>
    </div>
  );
}
